import { Box, Divider, Skeleton, Typography, useTheme, AppBar, Toolbar, Drawer } from "@mui/material";
import { ContactAvatar, Features, useSettings, DaymapIconType, StudentAvatar, useStudent, useApp, ShowTabsEnum, useAppState, ApiQueries } from "daymap-react";
import { useEffect, useRef, useState } from "react";
import { matchPath, useLocation, useMatch, useParams } from "react-router-dom";
import { AttentionMenuButton } from './AttentionMenuButton'

import styles from './styles.module.scss';
import { NavItem } from "..";

type StudentNavProps = {
    className?: string;
    studentId?: number;
}

export default function StudentNav({className, studentId}: StudentNavProps) {
    const match = useMatch("/student/:studentId/*");

    if (!match || !studentId)
        return null;

    return <Box className={className} display="flex" flexDirection="column" gap={4}>
        <Student studentId={studentId} />
        <MenuItems />
    </Box>
}

const Student: React.FC<{studentId:number}> = ({studentId}) => {
    const student = useStudent(studentId);

    if (!student)
        return <Skeleton variant="circular" width={80} height={80} />

    return <Box className={styles.student}>
        <ContactAvatar studentId={student?.ID} variant="rounded" contactFullName={student?.DisplaynameSurname} />
        <Typography>{student?.DisplaynameSurname}</Typography>
    </Box>
}

const rootMatchPath = '/student/:studentId';

type MenuItem = {
    index: number;
    title: string;
    to: string;
    iconType: DaymapIconType;
}

const coreTabs = [
    { index: 1, title: 'Profile', to: '', iconType: DaymapIconType.Profile },
    { index: 2, title: 'Timetable', to: '/timetable', iconType: DaymapIconType.Schedule },
    { index: 4, title: 'Schedule', to: '/schedule', iconType: DaymapIconType.CalendarMonth },
]

const MenuItems:React.FC = () => {
    const location = useLocation();
    const params = useParams();
    const { settings, hasFeature, hasTab } = useSettings();

    const [tabs, setTabs] = useState<MenuItem[]>(coreTabs);
    
    useEffect(() => {
        if (settings) {
            const updatedTabs: MenuItem[] = [...coreTabs];

            if (hasTab(ShowTabsEnum.Attendance))
                updatedTabs.push({ index: 6, title: 'Attendance', to: '/attendance', iconType: DaymapIconType.Attendance });
                
            if (settings?.pointsName && hasFeature(Features.StudentData))
                updatedTabs.push({ index: 10, title: settings?.pointsName, to: '/points', iconType: DaymapIconType.Points });

            if (hasFeature(Features.Curriculum) && hasTab(ShowTabsEnum.Results))
                updatedTabs.push({ index: 5, title: 'Assessment', to: '/assessment', iconType: DaymapIconType.Assessment });

            if (hasTab(ShowTabsEnum.Attachments))
                updatedTabs.push({ index: 9, title: 'Attachments', to: '/attachments', iconType: DaymapIconType.Attachment });

            if (hasTab(ShowTabsEnum.Reports))
                updatedTabs.push({ index: 8, title: 'Reports', to: '/reports', iconType: DaymapIconType.Report });

            if (hasFeature(Features.RecordManagement) && hasTab(ShowTabsEnum.Records))
                updatedTabs.push({ index: 7, title: 'Records', to: '/records', iconType: DaymapIconType.Record });

            if (hasTab(ShowTabsEnum.Notes))
                updatedTabs.push({ index: 11, title: 'Notes', to: '/notes', iconType: DaymapIconType.Note });

            updatedTabs.push({ index: 3, title: 'Classes', to: '/classes', iconType: DaymapIconType.Class });

            setTabs(updatedTabs)
        }
    }, [settings])
    
    const matchedTab = tabs.find(t => matchPath(`${rootMatchPath}${t.to}`, location.pathname));

    return <div className={styles.menu} role="navigation">
        {tabs.sort((a, b) => a.index - b.index).map(t => <NavItem key={t.title} title={t.title} iconType={t.iconType} to={`/student/${params.studentId}${t.to}`} selected={matchedTab?.title === t.title} />)}
    </div>
}

type MobileStudentHeaderProps = {
    studentId: number;
}
export const MobileStudentHeader: React.FC<MobileStudentHeaderProps> = ({studentId}) => {
    const theme = useTheme();
    const student = useStudent(studentId);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const location = useLocation();
    const { setStudentAppBarEl } = useApp();
    const appBarRef = useRef<HTMLDivElement | null>(null);
    const { data, isPending } = new ApiQueries.contact.profilesetting.Get({ settingName: "Portal_HideStudentMenuInfo"}).ToQuery();
    const [ state, setState ] = useAppState("Mobilestudentheader", { showBackdrop: true }, "session");

    useEffect(() => {
        return () => setStudentAppBarEl(null);
    }, [])

    useEffect(() => {
        if (drawerOpen)
            setDrawerOpen(false);
    }, [location])

    useEffect(() => {
        setStudentAppBarEl(appBarRef?.current);
    }, [appBarRef.current])

    let showBackdrop = state.showBackdrop && !isPending && !Boolean(data);

    return <>
        <Box>
            <AppBar ref={appBarRef} sx={{background: theme.palette.background.paper,  color: theme.palette.text.primary, paddingTop: 'env(safe-area-inset-top)'}}> 
                <Toolbar sx={{ justifyContent: "space-between"}}>
                    <AttentionMenuButton showBackdrop={showBackdrop} setShowBackdrop={show => setState({ showBackdrop: show })} onClick={() => setDrawerOpen(true)} />
                    <Typography>{student?.DisplaynameSurname}</Typography>
                    <StudentAvatar studentId={studentId} variant="rounded" />
                    </Toolbar>  
            </AppBar>
            <Divider />
        </Box>
        <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
            <Box p={1} sx={{paddingTop: `calc(env(safe-area-inset-top) + 25px)`}}>
                <MenuItems />
            </Box>
        </Drawer>
    </>
}

